import React from 'react';

import { analytics } from './analytics/analytics';
import { useCartApi } from './api/useCartApi';
import { useProductsApi } from './api/useProductsApi';
import { violetta } from './violettaSdk';

// Le card prodotto in chat hanno una CTA "aggiungi al carrello", e lo stesso vale
// per la CTA di una routine che li aggiunge tutti insieme, ma il carrello è dello
// store: violetta chiede con l'evento "addToCart" e aspetta la risposta
// "addToCartResult". Qui si eseguono le richieste e si risponde.
//
// Il payload del confine porta `items`, righe `{ skuId, quantity }` con la variante
// già scelta da violetta fra quelle disponibili: anche l'aggiunta di un solo sku
// arriva come lista di un elemento, non esiste una forma a riga singola. Non porta il
// prezzo, di proposito: è obbligatorio nel body di aggiunta, e accettarlo dall'iframe
// significherebbe inoltrare alle proprie api un valore non verificato. Va quindi
// risolto qui, con una chiamata per sku.

type AddToCartPayload = {
  items: Array<{ quantity: number, skuId: string; }>;
  requestId?: string;
};

// Registrazione una sola volta per caricamento di pagina, e non per istanza del
// componente: l'sdk accumula gli handler e non espone un "off", quindi ogni
// registrazione in più è un'aggiunta al carrello in più per ogni click. Un
// `useEffect` con dipendenze vuote NON basta: gli store girano in
// `React.StrictMode`, che in sviluppo esegue setup, cleanup e di nuovo setup, e
// qui un cleanup che disfi la registrazione non può esistere.
let registered = false;
// Per la stessa ragione l'handler registrato non può catturare `addSkus`/`getSku`
// dalla closure del primo mount: cambiano identità a ogni render, mentre la
// registrazione è una sola e per sempre. Il riferimento alla versione corrente sta
// nel modulo e non in una ref, e le due cose vanno insieme: `registered` misura la
// pagina, e se il riferimento misurasse l'istanza, dopo un remount l'handler già
// registrato continuerebbe a leggere una ref che nessuno scrive più — aggiunta al
// carrello morta in silenzio.
let currentHandler: ((payload: AddToCartPayload) => void) | undefined;

export const useViolettaAddToCart = (enable: boolean) => {
  const { addSkus } = useCartApi();
  const { getSku } = useProductsApi();

  const handleAddToCart = React.useCallback(
    async ({ items: requested, requestId }: AddToCartPayload) => {
      // Sku effettivamente entrati nel carrello. Parte vuoto e la risposta a violetta
      // è fuori dal try: nessun ramo, nemmeno un errore imprevisto, può lasciare la
      // CTA senza esito. Senza risposta resta in attesa 10 secondi e poi si sblocca
      // da sola, come se l'integrazione non ci fosse.
      const added = new Set<string>();

      try {
        // `silent: false` per l'overlay di caricamento del sito, come per ogni
        // altra aggiunta al carrello; `suppressErrorModal` perché il fallimento lo
        // comunica già la CTA con "riprova", e un modal di piattaforma sarebbe un
        // secondo messaggio per lo stesso errore.
        const resolved = await Promise.all(
          requested.map(async (item) => {
            try {
              const { data: sku } = await getSku(
                item.skuId,
                { populate: { price: true, product: true } },
                { silent: false, suppressErrorModal: true },
              );

              // Le guardie prima di `addSkus`: il budget è di 10 secondi, oltre i
              // quali la CTA ha già rinunciato e una risposta tardiva viene
              // ignorata, lasciando il prodotto nel carrello senza che si veda.
              // `sku.price` in particolare non è pignoleria: `addSkus` senza prezzo
              // lancia in modo sincrono. Uno sku scartato non blocca gli altri.
              if (sku.product === undefined) {
                // eslint-disable-next-line no-console
                console.warn(`[violetta] sku ${item.skuId} senza prodotto: aggiunta al carrello non tentata.`);
              } else if (sku.state !== 'AVAILABLE') {
                // eslint-disable-next-line no-console
                console.warn(
                  `[violetta] sku ${item.skuId} non disponibile (${sku.state}): aggiunta al carrello non tentata.`,
                );
              } else if (sku.price === undefined) {
                // eslint-disable-next-line no-console
                console.warn(`[violetta] sku ${item.skuId} senza prezzo: aggiunta al carrello non tentata.`);
              } else {
                return { product: sku.product, quantity: item.quantity, sku, skuId: item.skuId };
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.warn(`[violetta] risoluzione dello sku ${item.skuId} non riuscita.`, error);
            }

            return undefined;
          }),
        );
        const lines = resolved.filter((line): line is NonNullable<typeof line> => line !== undefined);

        if (lines.length > 0) {
          const response = await addSkus(
            lines.map((line) => ({ product: line.product, quantity: line.quantity, sku: line.sku })),
            { suppressErrorModal: true },
          );

          // Da chiamare qui: `addSkus` non traccia da sé (a differenza di
          // `removeSku`). Senza, le aggiunte che arrivano dalla chat sparirebbero
          // dai dati, cioè proprio quelle che si vorrà misurare.
          analytics.events.addToCart(
            response.eventId,
            lines.map((line) => ({ partialSku: line.sku, quantity: line.quantity })),
          );
          lines.forEach((line) => added.add(line.skuId));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[violetta] aggiunta al carrello non riuscita.', error);
      }

      // Gli `skuId` vanno rimandati IDENTICI a quelli ricevuti, senza normalizzarli,
      // e con il `requestId` ricevuto: sono le chiavi con cui la CTA riconosce la
      // risposta come propria. Per lo stesso motivo non si usa `sku.id`, che qui
      // sarebbe lo stesso valore ma solo per coincidenza.
      violetta.command('send', 'addToCartResult', {
        requestId,
        results: requested.map((item) => ({ ok: added.has(item.skuId), skuId: item.skuId })),
      });
    },
    [addSkus, getSku],
  );

  React.useEffect(() => {
    currentHandler = handleAddToCart;
  }, [handleAddToCart]);

  React.useEffect(() => {
    if (!enable || registered) {
      return;
    }

    registered = true;
    violetta.command('on', 'addToCart', (payload) => {
      currentHandler?.(payload);
    });
  }, [enable]);
};
